import { useRef, useEffect } from "react";
import { Search } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import { useFilter } from "./FilterContext";

export function FilterCategoryPicker() {
  const { t } = useTranslation(["common"]);
  const { categoryGroups, searchQuery, setSearchQuery, setStep } = useFilter();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Auto-focus search input when category picker opens
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, []);

  const filteredGroups = categoryGroups
    .map((group) => ({
      ...group,
      categories: group.categories.filter((cat) =>
        cat.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    }))
    .filter((group) => group.categories.length > 0);

  return (
    <div className="w-[280px]">
      {/* Search field */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
        <Search className="h-3.5 w-3.5 shrink-0 text-muted" strokeWidth={1.8} />
        <input
          ref={inputRef}
          type="text"
          placeholder={t("ui.filter.placeholder")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 bg-transparent text-[13px] text-primary placeholder:text-muted outline-none"
        />
      </div>

      {/* Category groups */}
      <div className="p-1 max-h-[320px] overflow-y-auto scrollbar-thin">
        {filteredGroups.map((group, groupIdx) => (
          <div key={group.label}>
            {groupIdx > 0 && <div className="h-px bg-border my-1 mx-2" />}
            <div className="px-3 py-1.5 text-xs font-medium text-muted uppercase">
              {group.label}
            </div>
            {group.categories.map((category) => (
              <button
                type="button"
                key={category.id}
                onClick={() =>
                  setStep({ type: "value_picker", categoryId: category.id })
                }
                className="flex w-full items-center gap-2.5 px-3 py-2 text-[13px] text-secondary hover:bg-card-hover hover:text-primary rounded-[8px] cursor-pointer transition-colors"
              >
                <category.icon className="h-4 w-4 shrink-0" strokeWidth={1.8} />
                {category.label}
              </button>
            ))}
          </div>
        ))}

        {filteredGroups.length === 0 && (
          <div className="px-3 py-4 text-[13px] text-muted text-center">
            {t("ui.filter.noMatchingFilters")}
          </div>
        )}
      </div>
    </div>
  );
}
